import { useCallback, useRef, useState } from 'react';

import { Box, ButtonBase, Card, IconButton, Typography } from '@mui/material';
import Grid from '@mui/material/Grid';
import { Image } from '@realadvisor/image';
import { ImagePlusIcon, Trash2Icon } from 'lucide-react';
import { useFormContext, useWatch } from 'react-hook-form';

import type { TransactionFormData } from '@modules/_shared/components/create-transaction/hooks/useTransactionStepper';
import { ImageGalleryDialog } from '@modules/_shared/components/image-upload/components/ImageGalleryDialog';
import {
  OnboardingImageDropzone,
  OnboardingImagePreview,
} from '@modules/_shared/components/image-upload/OnboardingImageDropzone';
import { useImageFilePicker } from '@modules/_shared/components/image-upload/useImageFilePicker';
import { useImageUpload } from '@modules/_shared/components/image-upload/useImageUpload';
import RASnackbar from '@modules/_shared/components/RASnackbar';
import { useLocale } from '@shared/hooks/locale';

import { useTransactionFormUi } from '../TransactionFormUi';
import { TransactionStepShell } from '../TransactionStepShell';

const PROPERTY_PHOTO_ASPECT_RATIO = 4 / 3;

type OnboardingPhotosUploadProps = {
  uploadedImage: TransactionFormData['photos']['uploadedImage'];
  listingImage: TransactionFormData['photos']['listingImage'];
  onRemove: () => void;
  onUpload: (image: { id: string; url: string }) => void;
  onUploadingChange?: (isUploading: boolean) => void;
};

const OnboardingPhotosUpload = ({
  uploadedImage,
  listingImage,
  onRemove,
  onUpload,
  onUploadingChange,
}: OnboardingPhotosUploadProps) => {
  const { t } = useLocale();
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { upload } = useImageUpload({
    onError: message => setError(message),
  });

  const uploadFile = async (file: File) => {
    setIsUploading(true);
    onUploadingChange?.(true);
    try {
      const result = await upload(file);
      if (result) {
        onUpload(result);
      }
    } finally {
      setIsUploading(false);
      onUploadingChange?.(false);
    }
  };

  const { dropZoneRef, dragOver, openFileDialog } = useImageFilePicker({
    onFile: file => void uploadFile(file),
  });

  const previewUrl = uploadedImage?.url;

  return (
    <>
      {previewUrl ? (
        <OnboardingImagePreview
          imageUrl={previewUrl}
          isUploading={isUploading}
          onReplace={openFileDialog}
          onRemove={onRemove}
          aspectRatio={PROPERTY_PHOTO_ASPECT_RATIO}
          replaceTooltip={t('Change photo')}
        />
      ) : listingImage ? (
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            aspectRatio: `${PROPERTY_PHOTO_ASPECT_RATIO} / 1`,
            minHeight: { xs: 200, md: 220 },
            maxHeight: { xs: 280, md: 320 },
            borderRadius: '16px',
            overflow: 'hidden',
            bgcolor: 'grey.200',
            boxShadow: '0 8px 24px rgba(15,23,42,0.10)',
            '& img': {
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            },
          }}
        >
          <Image
            src={{
              bucketName: listingImage.bucket_name,
              fileName: listingImage.file_name,
            }}
          />
        </Box>
      ) : (
        <OnboardingImageDropzone
          targetRef={dropZoneRef}
          dragOver={dragOver}
          isUploading={isUploading}
          onClick={openFileDialog}
          aspectRatio={PROPERTY_PHOTO_ASPECT_RATIO}
          uploadLabel={t('Drag or click to upload')}
          uploadLabelDragOver={t('Drop your photo')}
          hintText={t('JPG or PNG · landscape works best · max 5 MB')}
        />
      )}
      {error != null && (
        <RASnackbar message={error} onClose={() => setError(null)} />
      )}
    </>
  );
};

type PhotosStepProps = {
  onUploadingChange?: (isUploading: boolean) => void;
  skipPhotoLabel?: string;
};

export const PhotosStep: React.FC<PhotosStepProps> = ({
  onUploadingChange,
  skipPhotoLabel,
}) => {
  const { t } = useLocale();
  const formUi = useTransactionFormUi();
  const { setValue } = useFormContext<TransactionFormData>();
  const [dialogOpen, setDialogOpen] = useState(false);
  const skipLinkRef = useRef<HTMLButtonElement>(null);

  const uploadedImage = useWatch<TransactionFormData, 'photos.uploadedImage'>({
    name: 'photos.uploadedImage',
  });
  const listingImage = useWatch<TransactionFormData, 'photos.listingImage'>({
    name: 'photos.listingImage',
  });

  const handleSkipPhoto = useCallback(() => {
    const form = skipLinkRef.current?.closest('form');
    form?.requestSubmit();
  }, []);

  const showSkipLink =
    formUi === 'onboarding' && uploadedImage == null && listingImage == null;

  const handleUpload = async (images: { id: string; url: string }[]) => {
    if (images[0]) {
      setValue('photos.uploadedImage', images[0]);
    }
    setDialogOpen(false);
  };

  const handleRemove = () => {
    setValue('photos.uploadedImage', undefined);
  };

  const photoCard = (
    <Card
      variant="outlined"
      onClick={() => setDialogOpen(true)}
      sx={{
        position: 'relative',
        aspectRatio: '4/3',
        cursor: 'pointer',
        overflow: 'hidden',
        '&:hover': { borderColor: 'primary.main' },
        transition: 'border-color 0.2s',
      }}
    >
      {uploadedImage ? (
        <Box
          component="img"
          src={uploadedImage.url}
          alt={t('Property photo')}
          sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
      ) : listingImage ? (
        <Image
          src={{
            bucketName: listingImage.bucket_name,
            fileName: listingImage.file_name,
          }}
        />
      ) : (
        <Box
          sx={{
            width: '100%',
            height: '100%',
            bgcolor: 'grey.100',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
          }}
        >
          <ImagePlusIcon size={48} color={'var(--mui-palette-grey-400)'} />
          <Typography variant="body2" color="text.secondary">
            {t('Click to add photo')}
          </Typography>
        </Box>
      )}

      {uploadedImage && (
        <IconButton
          size="small"
          onClick={e => {
            e.stopPropagation();
            handleRemove();
          }}
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            bgcolor: 'background.paper',
            '&:hover': { bgcolor: 'error.light', color: 'white' },
          }}
        >
          <Trash2Icon size={18} />
        </IconButton>
      )}
    </Card>
  );

  const dialog = dialogOpen ? (
    <ImageGalleryDialog
      open
      maxWidth="sm"
      onClose={() => setDialogOpen(false)}
      entityId="temp"
      entityType="property"
      onSelectImage={() => Promise.resolve()}
      onFilesUploaded={handleUpload}
    />
  ) : null;

  return (
    <TransactionStepShell
      title={t('Photo')}
      subtitle={t('Add a photo of the property')}
      withFieldsGrid={formUi !== 'onboarding'}
    >
      {formUi === 'onboarding' ? (
        <>
          <OnboardingPhotosUpload
            uploadedImage={uploadedImage}
            listingImage={listingImage}
            onRemove={handleRemove}
            onUpload={image => setValue('photos.uploadedImage', image)}
            onUploadingChange={onUploadingChange}
          />
          {showSkipLink && (
            <Box sx={{ display: 'flex', justifyContent: 'center', pt: 0.5 }}>
              <ButtonBase
                ref={skipLinkRef}
                type="button"
                onClick={handleSkipPhoto}
                sx={{
                  fontSize: 13,
                  color: 'text.secondary',
                  textDecoration: 'underline',
                  textUnderlineOffset: 3,
                  py: 0.5,
                  px: 1,
                  borderRadius: 1,
                  '&:hover': { color: 'text.primary' },
                }}
              >
                {skipPhotoLabel ?? t('Add sale without photo')}
              </ButtonBase>
            </Box>
          )}
        </>
      ) : (
        <>
          <Grid size={{ xs: 12, sm: 6 }}>{photoCard}</Grid>
          {dialog}
        </>
      )}
    </TransactionStepShell>
  );
};
